import {
    useContext, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '@s20/services/httpClient';
import SlideForm from '../../../common/forms/SlideForm';
import { Textarea } from '../../../common/forms/Textarea';
import Input from '../../../common/forms/Input';
import { DatePicker } from '../../../common/forms/DatePicker';
import { Block } from '../../../common/Block';
import { ApiRoutes } from '../../../Common';
import { FormContext, FormCheckbox, toasty } from '../../../common/forms/FormElements';
import AddressFormNew from '../../../address/AddressFormNew';
import { Customer } from './Customer';
import { Foreman } from './Foreman';

export const RequestJobForm = ({ show, onClose }) => {
    const { values } = useContext(FormContext);
    const { jobDate, defaultTimesheetId } = values;

    const [isValidated, setIsValidated] = useState(false);
    const formRef = useRef(null);

    const queryClient = useQueryClient();

    // The form (and its values) remounts each time it opens, but this component stays
    // mounted, so a prior submit leaves isValidated=true and paints the fresh form red.
    // Reset validation each time the slideout opens.
    useEffect(() => {
        if (show) {
            setIsValidated(false);
        }
    }, [show]);

    const handleSubmit = useMutation({
        mutationFn: async (values) => {
            // The meeting location is the primary address (always entered) and lives under
            // dotted "meetingLocation.*" keys. Reshape it into the nested object the API
            // binds to.
            const meetingLocation = {
                address1: values['meetingLocation.address1'],
                address2: values['meetingLocation.address2'],
                city: values['meetingLocation.city'],
                state: values['meetingLocation.state'],
                usStateId: values['meetingLocation.usStateId'],
                zip: values['meetingLocation.zip'],
                latitude: values['meetingLocation.latitude'],
                longitude: values['meetingLocation.longitude'],
                timeZone: values['meetingLocation.timeZone'],
            };

            // The job location (top-level fields) mirrors the meeting location when the
            // user opts to keep them the same; otherwise it uses what they entered.
            const jobLocation = values.jobLocationSameAsMeeting
                ? meetingLocation
                : {
                    address1: values.address1,
                    address2: values.address2,
                    city: values.city,
                    state: values.state,
                    usStateId: values.usStateId,
                    zip: values.zip,
                    latitude: values.latitude,
                    longitude: values.longitude,
                    timeZone: values.timeZone,
                };

            const payload = {
                ...values,
                ...jobLocation,
                // The meeting location is always supplied explicitly, so the server should
                // use it as-is rather than copying the job location over.
                meetingLocationSameAsJobLocation: false,
                meetingLocation,
            };

            const response = await httpClient.post(
                ApiRoutes.jobRequest.jobRequest(),
                payload,
                httpClient.format.json,
            );

            if (response?.confirmationNumber) {
                toasty.success('Request Submitted', (
                    <>
                        <p>
                            Your job request
                            {' '}
                            <span className="font-semibold">#{response.confirmationNumber}</span>
                            {' '}
                            has been received.
                        </p>

                        <p>
                            You will receive a confirmation email shortly. Our team will review your request and follow up with you.
                        </p>
                    </>
                ));
                onClose();
            } else {
                toasty.error('An error occurred when submitting this job request');
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['jobRequests', jobDate, defaultTimesheetId] });
        },
    });

    const { data: customers, isLoading: loading } = useQuery({
        queryKey: ['jobRequestCustomers'],
        queryFn: () => httpClient.js(ApiRoutes.jobRequests.getCustomers()),
    });

    return createPortal(
        <SlideForm
            id="jobForm"
            name="jobForm"
            show={show}
            onClose={onClose}
            loading={loading}
            ref={formRef}
            formTitle="Request a Job"
            onSubmit={handleSubmit.mutateAsync}
            setIsValidated={(v) => setIsValidated(v)}
            isValidated={isValidated}
            initialValues={{
                jobLocationSameAsMeeting: true,
            }}
        >
            {({ values, setFieldValue }) => {
                // The address forms geocode against the selected customer's country;
                // without it the lookup can't resolve and no coordinates are captured.
                const selectedCustomer = customers?.find((customer) => customer.value === values.customerCompanyId);

                return (
                    <>
                        <Customer
                            customers={customers}
                            isLoading={loading}
                        />

                        <Foreman
                            customerCompanyId={values.customerCompanyId}
                        />

                        <Textarea
                            name="jobDescription"
                            label="Describe the work needed"
                            required
                            error="Please describe the work you are requesting"
                        />

                        <Input
                            name="workOrderNumber"
                            label="Work Order #"
                        />

                        <Input
                            name="purchaseOrderNumber"
                            label="Purchase Order #"
                        />

                        <DatePicker
                            name="requestedStartDate"
                            label="Requested Start Date"
                            required
                            error="Please provide a start date for the job"
                        />

                        <Textarea
                            name="notes"
                            label="Notes"
                        />

                        <div className="text-muted text-uppercase small mt-2">
                            Meeting Location
                        </div>

                        <Block>
                            <AddressFormNew
                                id="meetingAddressForm"
                                setFieldValue={setFieldValue}
                                countryId={selectedCustomer?.countryId}
                                countryCode={selectedCustomer?.countryCode}
                                namePrefix="meetingLocation"
                            />
                        </Block>

                        <FormCheckbox
                            small
                            className="ml-1 mb-2 mt-4"
                            id="jobLocationSameAsMeeting"
                            name="jobLocationSameAsMeeting"
                            labelText="Job location is the same as the meeting location"
                            onChange={(e) => {
                                // When unchecked, blank the job fields so the user enters a
                                // distinct job location, and drop stale validation styling.
                                // When checked, the job location mirrors the meeting location at
                                // submit time, so no field changes are needed here.
                                if (!e?.target?.checked) {
                                    [
                                        'address1', 'address2', 'city', 'state', 'usStateId',
                                        'zip', 'usCountyId', 'latitude', 'longitude', 'timeZone',
                                    ].forEach((field) => setFieldValue(field, undefined));
                                    setIsValidated(false);
                                }
                            }}
                        />

                        {!values.jobLocationSameAsMeeting && (
                            <>
                                <div className="text-muted text-uppercase small mt-2">
                                    Job Location
                                </div>

                                <Block>
                                    <AddressFormNew
                                        id="jobAddressForm"
                                        setFieldValue={setFieldValue}
                                        countryId={selectedCustomer?.countryId}
                                        countryCode={selectedCustomer?.countryCode}
                                        namePrefix=""
                                    />
                                </Block>
                            </>
                        )}
                    </>
                );
            }}
        </SlideForm>,
        document.body,
    );
};